import {useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {HelpIcon} from '@/components/help-icon'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {
  useCreateTag,
  useDeleteTag,
  useDuplicateTags,
  useMergeTags,
  useRenameTag,
  useTagsPage,
  type Tag as TagType,
  type TagDuplicatePair,
  type TagKind,
} from '@/hooks/empresa/organizacion/use-tags'
import {useAllEmployees} from '@/hooks/empresa/identidad/use-employees'
import {useSetAreaResponsable} from '@/hooks/empresa/organizacion/use-jerarquia'
import {PositionDetailDialog, TagPeopleList} from '@/components/tag-position-detail'
import {useTableState} from '@/hooks/common/use-table-state'
import {EmployeeSingleSelect} from '@/components/employee-single-select'
import {marcarErrorComoComunicado, translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

// Sedes live in Perfil de empresa's SedesSection, which reuses `TagList`.
const KINDS: TagKind[] = ['positions', 'areas']

/** El 409 usa el copy de esta pantalla y se marca a mano: si no, la red global pinta su burbuja y lo
 * manda a Sentry como no manejado. */
function renameError(err: unknown, clashMessage: string): string {
  const httpStatus = (err as {response?: {status?: number}}).response?.status
  if (httpStatus !== 409) return translateApiError(err)
  marcarErrorComoComunicado(err)
  return clashMessage
}

/** The resolved «jefe inmediato» reads off this plus jefe_directo (spec-incidentes-flujo §1.3). */
function AreaResponsableControl({businessId, tag, canEdit}: {businessId: string; tag: TagType; canEdit: boolean}) {
  const {t} = useTranslation()
  const {data: roster} = useAllEmployees(businessId)
  const setResponsable = useSetAreaResponsable(businessId)
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const current = roster?.find((e) => e.id === tag.responsable_employee_id) ?? null

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-1" data-testid={`area-responsable-${tag.id}`}>
      <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('jerarquia.responsableArea')}
      </span>
      {!editing ? (
        <>
          <span className="text-sm text-foreground" data-testid={`area-responsable-name-${tag.id}`}>
            {tag.responsable_nombre ?? t('jerarquia.sinResponsable')}
          </span>
          {canEdit && (
            <button
              type="button"
              data-testid={`area-responsable-edit-${tag.id}`}
              onClick={() => {
                setError(null)
                setEditing(true)
              }}
              className="text-xs font-medium text-primary hover:underline">
              {tag.responsable_employee_id ? t('jerarquia.change') : t('jerarquia.assign')}
            </button>
          )}
        </>
      ) : (
        <div className="w-full space-y-2">
          <EmployeeSingleSelect
            roster={roster ?? []}
            value={current}
            testId={`area-responsable-picker-${tag.id}`}
            onChange={(person) => {
              setError(null)
              setResponsable.mutate(
                {areaId: tag.id, responsableEmployeeId: person?.id ?? null},
                {onSuccess: () => setEditing(false), onError: (err) => setError(translateApiError(err))}
              )
            }}
          />
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-xs font-medium text-muted-foreground hover:underline">
            {t('common.cancel')}
          </button>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      )}
    </div>
  )
}

function TagRow({
  businessId,
  kind,
  tag,
  mergeMode,
  checked,
  onToggle,
  canEdit,
}: {
  businessId: string
  kind: TagKind
  tag: TagType
  mergeMode: boolean
  checked: boolean
  onToggle: () => void
  canEdit: boolean
}) {
  const {t} = useTranslation()
  const rename = useRenameTag(businessId, kind)
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(tag.name)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [peopleOpen, setPeopleOpen] = useState(false)
  const remove = useDeleteTag(businessId, kind)
  const isPosition = kind === 'positions'

  function startEditing() {
    setName(tag.name)
    setError(null)
    setEditing(true)
  }

  function save() {
    if (!name.trim()) return
    rename.mutate(
      {id: tag.id, name: name.trim()},
      {
        onSuccess: () => setEditing(false),
        onError: (err) => setError(renameError(err, t('tags.renameClash'))),
      }
    )
  }

  if (editing) {
    return (
      <li className="space-y-2 py-2">
        <div className="flex items-center gap-2">
          <Input
            value={name}
            maxLength={120}
            autoFocus
            data-testid="tag-rename-input"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
          />
          <Button size="sm" data-testid="tag-rename-save" disabled={rename.isPending || !name.trim()} onClick={save}>
            {t('tags.renameSave')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            {t('common.cancel')}
          </Button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </li>
    )
  }

  return (
    <li className="py-2" data-testid="tag-row">
      <div className="flex items-center gap-3">
        {mergeMode && (
          <input
            type="checkbox"
            data-testid="tag-merge-checkbox"
            checked={checked}
            onChange={onToggle}
            aria-label={t('tags.merge.select', {name: tag.name})}
            className="size-4 accent-primary"
          />
        )}
        <span className="min-w-0 flex-1 truncate text-sm text-foreground" data-testid="tag-name">
          {tag.name}
        </span>
        {isPosition || tag.usage_count > 0 ? (
          <button
            type="button"
            data-testid="tag-usage-chip"
            onClick={() => setPeopleOpen((open) => !open)}
            className="cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring/50">
            <Tag tone="neutral">{t('tags.usage', {count: tag.usage_count})}</Tag>
          </button>
        ) : (
          <Tag tone="neutral">{t('tags.usage', {count: tag.usage_count})}</Tag>
        )}
        {!mergeMode && canEdit && (
          <>
            <Button size="sm" variant="ghost" data-testid="tag-rename" onClick={startEditing}>
              {t('tags.rename')}
            </Button>
            <Button size="sm" variant="ghost" data-testid="tag-delete" onClick={() => setConfirming(true)}>
              {t('tags.delete')}
            </Button>
          </>
        )}
      </div>
      {kind === 'areas' && !mergeMode && <AreaResponsableControl businessId={businessId} tag={tag} canEdit={canEdit} />}
      {!isPosition && peopleOpen && (
        <TagPeopleList businessId={businessId} kind={kind} tagId={tag.id} showDescripcion={false} />
      )}
      {isPosition && (
        <PositionDetailDialog
          businessId={businessId}
          position={tag}
          canEdit={canEdit}
          open={peopleOpen}
          onOpenChange={setPeopleOpen}
        />
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('tags.deleteTitle', {name: tag.name})}
        description={t('tags.deleteBody', {count: tag.usage_count})}
        confirmLabel={t('tags.deleteConfirm')}
        cancelLabel={t('common.cancel')}
        pending={remove.isPending}
        onConfirm={() => remove.mutate(tag.id, {onSuccess: () => setConfirming(false)})}
      />
    </li>
  )
}

function DuplicateSuggestions({
  pairs,
  onMergePair,
  contenedor,
}: {
  pairs: TagDuplicatePair[]
  onMergePair: (pair: TagDuplicatePair) => void
  contenedor?: HTMLElement | null
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-2 rounded-lg border border-input bg-secondary/50 p-3" data-testid="tag-duplicates-strip">
      <div className="flex items-center gap-1.5">
        <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('tags.duplicates.title')}
        </p>
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('tags.duplicates.title')})}
          testId="tag-duplicates-tip"
          contenedor={contenedor}>
          {t('tags.duplicates.tip')}
        </HelpIcon>
      </div>
      <ul className="space-y-1.5">
        {pairs.map((pair) => (
          <li key={`${pair.a.id}-${pair.b.id}`} className="flex items-center gap-2" data-testid="tag-duplicate-pair">
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">
              {t('tags.duplicates.pair', {a: pair.a.name, b: pair.b.name})}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="tag-duplicate-merge"
              onClick={() => onMergePair(pair)}>
              {t('tags.duplicates.merge')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}

// Inline, not a nested Radix dialog: stacking two dialogs leaves a scroll-lock artifact.
function MergeConfirmPanel({
  businessId,
  kind,
  tags,
  onDone,
  onCancel,
}: {
  businessId: string
  kind: TagKind
  tags: TagType[]
  onDone: () => void
  onCancel: () => void
}) {
  const {t} = useTranslation()
  const [survivorId, setSurvivorId] = useState(tags[0]?.id ?? '')
  const [error, setError] = useState<string | null>(null)
  const merge = useMergeTags(businessId, kind)

  function confirm() {
    setError(null)
    const loserIds = tags.filter((tag) => tag.id !== survivorId).map((tag) => tag.id)
    merge.mutate({survivorId, loserIds}, {onSuccess: onDone, onError: (err) => setError(translateApiError(err))})
  }

  return (
    <div className="space-y-4" data-testid="tag-merge-confirm">
      <p className="text-sm text-muted-foreground">{t('tags.merge.explain')}</p>
      <ul className="space-y-1.5">
        {tags.map((tag) => (
          <li key={tag.id}>
            <label className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-input px-2.5 py-2 has-checked:border-primary">
              <input
                type="radio"
                name="merge_survivor"
                value={tag.id}
                checked={survivorId === tag.id}
                onChange={() => setSurvivorId(tag.id)}
                className="accent-primary"
              />
              <span className="min-w-0 flex-1 truncate text-sm">{tag.name}</span>
              <Tag tone="neutral">{t('tags.usage', {count: tag.usage_count})}</Tag>
            </label>
          </li>
        ))}
      </ul>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="button" disabled={merge.isPending} onClick={confirm} data-testid="tag-merge-confirm-button">
          {t('tags.merge.confirm')}
        </Button>
      </DialogFooter>
    </div>
  )
}

// Sin sedes: tienen su propio formulario en Perfil de empresa, con más campos que el nombre.
function CreateTagForm({businessId, kind}: {businessId: string; kind: 'areas' | 'positions'}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const create = useCreateTag(businessId, kind)

  function submit() {
    if (!name.trim()) return
    setError(null)
    create.mutate(name.trim(), {
      onSuccess: () => {
        setName('')
        setOpen(false)
      },
      onError: (err) => setError(renameError(err, t('tags.create.clash'))),
    })
  }

  if (!open) {
    return (
      <Button type="button" size="sm" variant="outline" data-testid="tag-create-open" onClick={() => setOpen(true)}>
        {t('tags.create.open', {kind: t(`tags.kindSingular.${kind}`)})}
      </Button>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          value={name}
          maxLength={120}
          autoFocus
          placeholder={t('tags.create.placeholder', {kind: t(`tags.kindSingular.${kind}`)})}
          data-testid="tag-create-input"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
        />
        <Button size="sm" data-testid="tag-create-submit" disabled={create.isPending || !name.trim()} onClick={submit}>
          {t('tags.create.submit')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setOpen(false)
            setError(null)
            setName('')
          }}>
          {t('common.cancel')}
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

export function TagList({
  businessId,
  kind,
  open,
  canEdit = true,
  contenedor,
}: {
  businessId: string
  kind: TagKind
  open: boolean
  canEdit?: boolean
  contenedor?: HTMLElement | null
}) {
  const {t} = useTranslation()
  const tabla = useTableState('name')
  const {data, isLoading} = useTagsPage(businessId, kind, {page: tabla.page, search: tabla.search}, open)
  // Read-only surfaces skip the fetch: the strip's only action would be disabled.
  const {data: duplicatePairs} = useDuplicateTags(businessId, kind, open && canEdit)
  const [mergeMode, setMergeMode] = useState(false)
  // Mapa y no `Set`: la fusión junta cargos de páginas distintas y el panel necesita el objeto.
  const [selected, setSelected] = useState<Map<string, TagType>>(new Map())
  const [confirming, setConfirming] = useState(false)

  if (isLoading && !data) return <Skeleton className="h-32 w-full" />
  const tags = data?.items ?? []
  const pairs = duplicatePairs ?? []

  function toggle(tag: TagType) {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(tag.id)) next.delete(tag.id)
      else next.set(tag.id, tag)
      return next
    })
  }

  function exitMergeMode() {
    setMergeMode(false)
    setSelected(new Map())
    setConfirming(false)
  }

  // Pre-fill, never auto-merge: the owner still picks the survivor and confirms.
  function mergePair(pair: TagDuplicatePair) {
    setSelected(
      new Map([
        [pair.a.id, pair.a],
        [pair.b.id, pair.b],
      ])
    )
    setMergeMode(true)
    setConfirming(true)
  }

  if (confirming) {
    return (
      <MergeConfirmPanel
        businessId={businessId}
        kind={kind}
        tags={[...selected.values()]}
        onDone={exitMergeMode}
        onCancel={() => setConfirming(false)}
      />
    )
  }

  return (
    <div className="space-y-3">
      {pairs.length > 0 && canEdit && !mergeMode && (
        <DuplicateSuggestions pairs={pairs} onMergePair={mergePair} contenedor={contenedor} />
      )}
      {kind !== 'sedes' && canEdit && !mergeMode && <CreateTagForm businessId={businessId} kind={kind} />}
      <Input
        maxLength={tope('BUSCA')}
        value={tabla.searchInput}
        onChange={(e) => tabla.setSearchInput(e.target.value)}
        placeholder={t('tags.searchPlaceholder', {kind: t(`tags.kind.${kind}`)})}
        aria-label={t('tags.searchPlaceholder', {kind: t(`tags.kind.${kind}`)})}
        data-testid="tag-search-input"
      />
      {tags.length > 0 && canEdit && (
        <div className="flex items-center justify-between">
          <Button
            type="button"
            size="sm"
            variant={mergeMode ? 'default' : 'outline'}
            data-testid="tags-merge-toggle"
            onClick={() => (mergeMode ? exitMergeMode() : setMergeMode(true))}>
            {mergeMode ? t('tags.merge.exit') : t('tags.merge.start')}
          </Button>
          {mergeMode && (
            <Button
              type="button"
              size="sm"
              disabled={selected.size < 2}
              data-testid="tags-merge-continue"
              onClick={() => setConfirming(true)}>
              {t('tags.merge.continue', {count: selected.size})}
            </Button>
          )}
        </div>
      )}
      {tags.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {tabla.search ? t('tags.searchEmpty') : t(`tags.empty.${kind}`)}
        </p>
      ) : (
        <ul className="divide-y divide-border" data-testid="tag-list">
          {tags.map((tag) => (
            <TagRow
              key={tag.id}
              businessId={businessId}
              kind={kind}
              tag={tag}
              mergeMode={mergeMode}
              checked={selected.has(tag.id)}
              onToggle={() => toggle(tag)}
              canEdit={canEdit}
            />
          ))}
        </ul>
      )}
      {data && data.total > 0 && (
        <PaginationBar page={data.page} pageSize={data.page_size} total={data.total} onPageChange={tabla.setPage} />
      )}
    </div>
  )
}

export function TagsManagerDialog({businessId, trigger}: {businessId: string; trigger: ReactNode}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<TagKind>('positions')
  // Ref de callback: sin él la ayuda se porta al body y su Escape cierra el diálogo entero.
  const [contenedor, setContenedor] = useState<HTMLElement | null>(null)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent ref={setContenedor} className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('tags.manageTitle')}</DialogTitle>
        </DialogHeader>
        <div className="flex w-fit overflow-hidden rounded-lg border border-input" role="group">
          {KINDS.map((k, i) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              data-testid={`tags-kind-${k}`}
              onClick={() => setKind(k)}
              className={cn(
                'px-3 py-1.5 text-sm whitespace-nowrap',
                i > 0 && 'border-l border-input',
                kind === k ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
              )}>
              {t(`tags.kind.${k}`)}
            </button>
          ))}
        </div>
        <TagList key={kind} businessId={businessId} kind={kind} open={open} contenedor={contenedor} />
      </DialogContent>
    </Dialog>
  )
}
